import React, { useState, useCallback, useEffect, useRef } from 'react';
import DateFnsUtils from '@date-io/date-fns';
import {
  startOfDay,
  endOfDay,
  startOfMinute,
  endOfMinute,
  parse
} from 'date-fns';
import { MuiPickersUtilsProvider } from 'material-ui-pickers';
import { useGridFilter } from 'ag-grid-react';
import DateTimeRange from './datetime';
import appConstant from '../../../../../appConstants';

const DateTimeFilter = (props) => {
  const {
    DATETYPE,
    isTimeInterval: isTimeIntervalProp,
    isFullRangeFilter: isFullRangeFilterProp,
    getValue,
    useGetterValue,
    colDef,
    passEmpty,
    model,
    onModelChange,
    context
  } = props;

  const dateType = DATETYPE || appConstant.DATETYPES.DATE;
  const isTimeInterval = Boolean(isTimeIntervalProp);
  const isFullRangeFilter = Boolean(isFullRangeFilterProp);
  const isInternalUpdate = useRef(false); // Track when we're making internal changes to avoid clearing on our own model updates

  const [state, setState] = useState({
    startDateTime: model?.[0]?.startDateTime || null,
    endDateTime: model?.[0]?.endDateTime || null
  });

  useEffect(() => {
    if ((!model || model.length === 0) && !isInternalUpdate.current) { // Only clear both inputs if it's an external clear all operation (not our own internal update)
      setState({ startDateTime: null, endDateTime: null });
    }
    isInternalUpdate.current = false;
  }, [model]);

  const getCurrentValue = useCallback(
    (params) => {
      let currentValue = getValue(params.node);
      if (currentValue) {
        currentValue =
          !useGetterValue && colDef.valueFormatter
            ? colDef.valueFormatter({ value: currentValue, data: params.data, context: context })
            : currentValue;
      }
      return currentValue;
    },
    [getValue, useGetterValue, colDef, context]
  );

  const doesFilterPass = useCallback(
    (params) => {
      const { startDateTime, endDateTime } = state;
      let currentValue = getCurrentValue(params);
      if (currentValue) {
        if (startDateTime && endDateTime) {
          // convert value single value to array so condition is run only once
          let timeWindows = Array.isArray(currentValue) ? currentValue : currentValue.split(',');
          if (isFullRangeFilter) {
            // check current starttime and current endtime with filter
            // it should be "greater than or equal to" the filter starttime value, and "less than or equal to" the filter endtime value.
            return (
              timeWindows.findIndex((timeWindow) => {
                let [currentStartTime, currentEndTime] = timeWindow
                  .split(dateType.intervalSeperator)
                  .map((time) => parse(time.trim(), dateType.format, new Date()));
                return currentStartTime >= startDateTime && currentEndTime <= endDateTime;
              }) > -1
            );
          }
          if (isTimeInterval) {
            timeWindows = timeWindows.map((timeWindow) =>
              timeWindow.split(dateType.intervalSeperator)[0].trim()
            );
          }

          return (
            timeWindows.findIndex((curTimeWindow) => {
              const timeWindow = parse(curTimeWindow, dateType.format, new Date());
              return timeWindow >= startDateTime && timeWindow <= endDateTime;
            }) > -1
          );
        }
        return false;
      }
      return passEmpty || false;
    },
    [state, getCurrentValue, dateType, isFullRangeFilter, isTimeInterval, passEmpty]
  );

  const isFilterActive = useCallback(() => {
    return Boolean(state.startDateTime) && Boolean(state.endDateTime);
  }, [state.startDateTime, state.endDateTime]);

  const onDateChange = useCallback(
    (field, value) => {
      // reset seconds when filter column has time component, reset time to start and end of day when filter column doesnt have time component
      // this is will help while filter since we can directly compare date objects
      if (value) {
        if (
          [appConstant.DATETYPES.TIME.type, appConstant.DATETYPES.DATETIME.type].includes(
            dateType.type
          )
        ) {
          if (field === 'startDateTime') {
            value = startOfMinute(value);
          } else {
            value = endOfMinute(value);
          }
        } else if (dateType.type === appConstant.DATETYPES.DATE.type) {
          if (field === 'startDateTime') {
            value = startOfDay(value);
          } else {
            value = endOfDay(value);
          }
        }
      } else {
        value = null;
      }
      const newState = {
        ...state,
        [field]: value
      };
      setState(newState);
      notifyFilterChanged(newState);
    },
    [state, dateType, onModelChange]
  );

  const notifyFilterChanged = useCallback((newState) => {
    const { startDateTime, endDateTime } = newState;
    const newModel = startDateTime && endDateTime
      ? [{ startDateTime, endDateTime }]
      : null;
    isInternalUpdate.current = newModel === null;
    onModelChange(newModel);
  }, [onModelChange]);

  // Register the filter with AG Grid
  useGridFilter({
    doesFilterPass,
    isFilterActive
  });

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <DateTimeRange
        dateType={dateType}
        startDateTime={state.startDateTime}
        endDateTime={state.endDateTime}
        startDateChanged={(date) => onDateChange('startDateTime', date)}
        endDateChanged={(date) => onDateChange('endDateTime', date)}
      />
    </MuiPickersUtilsProvider>
  );
};

export default DateTimeFilter;
